import api from "./api";

const TOKEN_KEY = "antique_tracker_access";
const REFRESH_KEY = "antique_tracker_refresh";

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface AuthResponse {
  user: AuthUser;
  access: string;
  refresh: string;
}

// ─── Token Storage ──────────────────────────────────────────

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// ─── API Calls ──────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>("/users/login/", { email, password }),

  register: (data: {
    token: string;
    first_name: string;
    last_name: string;
    password: string;
  }) => api.post<AuthResponse>("/users/register/", data),

  validateInvite: (token: string) =>
    api.get<{ email: string }>(`/users/register/?token=${token}`),

  me: () => api.get<AuthUser>("/users/me/"),

  refreshToken: (refresh: string) =>
    api.post<{ access: string }>("/users/token/refresh/", { refresh }),

  invite: (email: string) => api.post("/users/invites/", { email }),

  listInvites: () => api.get("/users/invites/list/"),
};

// ─── Axios Interceptors ─────────────────────────────────────

// Attach access token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/users/login") &&
      !originalRequest.url?.includes("/users/token/refresh")
    ) {
      originalRequest._retry = true;
      const refresh = getRefreshToken();

      if (refresh) {
        try {
          const res = await authApi.refreshToken(refresh);
          setTokens(res.data.access, refresh);
          originalRequest.headers.Authorization = `Bearer ${res.data.access}`;
          return api(originalRequest);
        } catch {
          clearTokens();
          window.location.href = "/login";
        }
      } else {
        clearTokens();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);
